<template>
  <div class="w-full relative h-full">
    <div class="map-responsive h-full">
      <div id="map" />
      <PageLoader v-if="!tenant">
        <template #body>
          <div>
            <i class="fas fa-map-marker-alt"></i>
            Loading Map...
          </div>
        </template>
      </PageLoader>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapActions, mapState } from "vuex";
import cable from "@/lib/cable";

import PageLoader from "@/components/PageLoader.vue";
import MapChannel from "@/channels/MapChannel";
import directionsDecoder from "@/directionsDecoder.js";
import appointmentMarkerIcon from "@/images/marker.svg?raw";
import technicianDefaultMarker from "@/images/technician-marker.svg?raw";

export default {
  components: {
    PageLoader
  },
  mixins: [directionsDecoder],
  data: function () {
    return {
      channel: undefined,
      appointments: [],
      technicians: [],
      map: undefined
    };
  },
  computed: {
    ...mapState(["tenant"])
  },
  watch: {
    tenant(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.init();
      }
    }
  },
  mounted() {
    this.fetchTenant();
  },
  beforeUnmount() {
    if (this.channel) this.channel.disconnect();
  },
  methods: {
    ...mapActions(["fetchTenant"]),
    init() {
      if (!this.map) {
        // Default map center in DFW
        let center = {
          lat: 32.934107,
          lng: -97.076533
        };
        if (this.tenant.map_center) {
          center = {
            lat: this.tenant.map_center.latitude,
            lng: this.tenant.map_center.longitude
          };
        }
        const element = document.getElementById("map");
        this.map = new window.google.maps.Map(element, {
          center,
          zoom: 12
        });

        const overlay = new window.google.maps.OverlayView();
        overlay.draw = function () {
          this.getPanes().markerLayer.id = "markerLayer";
        };
        overlay.setMap(this.map);

        this.addTechniciansLastLocation();
        this.listenForCableUpdates();
        this.getAllAppointments();
        this.listenTechnicianSelection();
      }
    },
    url_params() {
      const params = {};
      const url = window.location.search;
      const regex = /[?&;](.+?)=([^&;]+)/g;
      let match;
      if (url) {
        while ((match = regex.exec(url))) {
          params[match[1]] = decodeURIComponent(match[2]);
        }
      }
      return params;
    },
    listenForCableUpdates() {
      this.channel = new MapChannel();

      cable
        .subscribe(this.channel)
        .ensureSubscribed()
        .then(() => {
          this.channel.on("technician-location", (payload) => {
            const technician = this.technicians.find(
              (item) => item.id === payload.technicianId
            );
            technician
              ? this.updateTechnician(payload, technician)
              : this.addTechnician(payload);
          });
        })
        .catch((err) => {
          console.log("App Unable to join Cable server", err);
        });
    },
    addAppointments(appointments) {
      appointments.map((item) => {
        this.addAppointment(item);
      });
    },
    addAppointment(appointment) {
      const point = new window.google.maps.LatLng(
        appointment.latitude,
        appointment.longitude
      );
      const marker_map = new window.google.maps.Marker({
        position: point,
        map: this.map,
        icon: {
          url:
            "data:image/svg+xml;charset=UTF-8;base64," +
            btoa(appointmentMarkerIcon),
          size: new window.google.maps.Size(38, 55)
        },
        animation: window.google.maps.Animation.DROP
      });
      const marker_data = { data: appointment, marker: marker_map };
      this.appointments.push(marker_data);
      this.addMarkerListener(marker_map, marker_data);
    },
    addTechnician(params) {
      if (!params.position) {
        return;
      }
      let route = undefined;
      const point = new window.google.maps.LatLng(
        params.position.latitude,
        params.position.longitude
      );
      const marker = new window.google.maps.Marker({
        position: point,
        map: this.map,
        title: this.technicianName(params.technicianData),
        icon: this.setTechnicianIcon(params.technicianData),
        animation: window.google.maps.Animation.DROP
      });
      const infowindow = new window.google.maps.InfoWindow({
        headerContent: this.technicianInfoHeader(params.technicianData)
      });
      const technicianData = {
        id: params.technicianId,
        marker: marker,
        infowindow: infowindow,
        created_at: params.position.created_at,
        data: params.technicianData,
        route: route
      };
      marker.addListener("click", () => this.handleMarkerClick(technicianData));
      if (params.appointment && params.appointment.address) {
        const appointment_point = new window.google.maps.LatLng(
          params.appointment.address.latitude,
          params.appointment.address.longitude
        );
        route = this.setAppointmentRoute(point, appointment_point);
      }
      this.technicians.push(technicianData);
    },
    handleMarkerClick(technician) {
      technician.infowindow.setContent(
        this.technicianInfoContent(technician.created_at)
      );
      technician.infowindow.open({
        anchor: technician.marker
      });
    },
    updateTechnician(params, technician) {
      const point = new window.google.maps.LatLng(
        params.position.latitude,
        params.position.longitude
      );
      technician.marker.setPosition(point);
      technician.created_at = params.position.created_at;
      technician.infowindow.setContent(
        this.technicianInfoContent(technician.created_at)
      );
    },
    // Built from technicianData already loaded with the marker, so revealing the
    // name costs no request. The marker title is the hover affordance; the
    // infowindow carries it too because touch devices have no hover.
    technicianName(technicianData) {
      if (!technicianData) return "";

      return [technicianData.first_name, technicianData.last_name]
        .filter(Boolean)
        .join(" ");
    },
    // The name sits in the infowindow header, level with the close button, so
    // the bubble reads as a title over a muted timestamp. Both are built as
    // nodes with textContent so a name is never parsed as HTML.
    technicianInfoHeader(technicianData) {
      const name = this.technicianName(technicianData);
      if (!name) return null;

      const header = document.createElement("div");
      header.className = "text-sm font-semibold text-gray-900";
      header.textContent = name;
      return header;
    },
    technicianInfoContent(createdAt) {
      const content = document.createElement("div");
      content.className = "pb-2 pr-3 text-xs text-gray-500";
      content.textContent = `Last updated ${moment(createdAt).fromNow()}`;
      return content;
    },
    setAppointmentRoute(origin, destination) {
      const directionsDisplay = new window.google.maps.DirectionsRenderer();
      const directionsService = new window.google.maps.DirectionsService();
      directionsDisplay.setMap(this.map);
      directionsDisplay.setOptions({
        suppressMarkers: true,
        preserveViewport: true,
        polylineOptions: {
          strokeColor: "#05ACD5",
          strokeWeight: 4,
          geodesic: true
        }
      });
      directionsService.route(
        {
          destination: destination,
          origin: origin,
          travelMode: "DRIVING",
          provideRouteAlternatives: false
        },
        (response, status) => {
          if (status === "OK") {
            directionsDisplay.setDirections(response);
          } else {
            return status;
          }
        }
      );

      return directionsDisplay;
    },
    setTechnicianIcon(technician) {
      if (technician && technician.avatar && technician.avatar.url) {
        return {
          url: technician.avatar.url,
          scaledSize: new window.google.maps.Size(44, 44)
        };
      } else {
        return {
          url:
            "data:image/svg+xml;charset=UTF-8;base64," +
            btoa(technicianDefaultMarker),
          scaledSize: new window.google.maps.Size(44, 44)
        };
      }
    },
    addTechniciansLastLocation() {
      const today = moment(new Date()).format("DD/MM/YYYY");
      const params = Object.assign({}, { date: today });
      axios
        .get("/technicians/available_technicians_location", { params: params })
        .then(
          (response) => {
            response.data.map((item) => {
              this.addTechnician(item);
            });
          },
          (error) => {
            console.log(error);
            // error callback
          }
        );
    },
    listenTechnicianSelection() {
      const component = this;
      const connection = axios;
      $(document).on("change", "#technician_map", function () {
        const technician_id = $("#technician_map option:selected").val();
        component.emptyAppointments();
        if (technician_id != 0) {
          component.hideTechniciansExcept(technician_id);
          connection
            .get("dashboard/technician_next_appointment", {
              params: { id: technician_id }
            })
            .then(
              (response) => {
                if (response.data != null) {
                  component.addAppointment(response.data);
                }
              },
              (response) => {
                console.log(response);
                // error callback
              }
            );
        } else {
          component.getAllAppointments();
          component.showAllTechnicians();
        }
      });
    },
    hideTechniciansExcept(technicianId) {
      for (let i = 0; i < this.technicians.length; i++) {
        const technician = this.technicians[i];
        if (technician.id != technicianId) {
          technician.marker.setVisible(false);
          if (technician.route) {
            technician.route.setMap(null);
          }
        } else {
          technician.marker.setVisible(true);
          if (technician.route) {
            technician.route.setMap(this.map);
          }
        }
      }
    },
    showAllTechnicians() {
      for (let i = 0; i < this.technicians.length; i++) {
        const technician = this.technicians[i];
        technician.marker.setVisible(true);
        if (technician.route) {
          technician.route.setMap(this.map);
        }
      }
    },
    emptyAppointments() {
      for (let i = 0; i < this.appointments.length; i++) {
        this.appointments[i]["marker"].setMap(null);
      }
      this.appointments = [];
    },
    getAllAppointments() {
      const today = moment(new Date()).format("MM/DD/YYYY");
      const params = Object.assign(
        {},
        { records_only: true, not_cancelled: true, service_date: today },
        this.url_params()
      );
      axios.get("/appointments/map_list", { params: params }).then(
        (response) => {
          this.addAppointments(response.data);
        },
        (response) => {
          console.log(response);
          // error callback
        }
      );
    },
    addMarkerListener: function (marker_map, marker_data) {
      window.google.maps.event.addListener(marker_map, "click", function () {
        window.location.href = "jobs/" + marker_data["data"].job_id;
      });
    }
  }
};
</script>

<style scoped></style>
